import { Component, inject, Input, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { FikoCollabService } from '../../services/fiko-collab.service';
import { FikoCollabUserListComponent } from './fiko-collab-user-list.component';
import { MarkupType, SceneOperationType, FileShareReadyData } from './fiko-collab-types';

/**
 * FikoCollabDialogComponent - Compact collaboration control panel
 *
 * Features:
 * - Connection status
 * - Room join/leave
 * - User list with follow controls
 * - Markup annotation tools
 */
@Component({
    selector: 'fiko-collab-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, FikoCollabUserListComponent],
    template: `
        <div class="collab-dialog">
            <!-- Connection Section -->
            <div class="section connection-section">
                <div class="section-header">
                    <span class="status-dot" [class.online]="collabService.isConnected()"></span>
                    <span class="section-title">Baglanti</span>
                </div>

                @if (!collabService.isConnected()) {
                    <div class="connection-form">
                        <input
                            type="text"
                            class="fiko-input"
                            placeholder="Sunucu URL"
                            [(ngModel)]="serverUrl"
                        />
                        <input
                            type="text"
                            class="fiko-input"
                            placeholder="Kullanici Adi"
                            [(ngModel)]="userName"
                        />
                        <button class="fiko-btn primary" (click)="connect()">
                            <i class="plug icon"></i> Baglan
                        </button>
                    </div>
                } @else {
                    <div class="connection-info">
                        <span class="user-label">{{ userName }}</span>
                        <button class="fiko-btn danger small" (click)="disconnect()">
                            <i class="power icon"></i>
                        </button>
                    </div>
                }
            </div>

            <!-- Room Section -->
            @if (collabService.isConnected()) {
                <div class="section room-section">
                    <div class="section-header">
                        <i class="door open icon"></i>
                        <span class="section-title">Oda</span>
                    </div>

                    @if (!collabService.currentRoomId()) {
                        <div class="room-form">
                            <input
                                type="text"
                                class="fiko-input"
                                placeholder="Oda ID"
                                [(ngModel)]="roomId"
                            />
                            <button class="fiko-btn primary" (click)="joinRoom()">
                                <i class="sign in icon"></i> Katil
                            </button>
                        </div>
                    } @else {
                        <div class="room-info">
                            <span class="room-label">{{ collabService.currentRoomId() }}</span>
                            <button class="fiko-btn warning small" (click)="leaveRoom()">
                                <i class="sign out icon"></i>
                            </button>
                        </div>
                    }
                </div>
            }

            <!-- Users Section -->
            @if (collabService.currentRoomId()) {
                <div class="section users-section">
                    <fiko-collab-user-list />
                </div>
            }

            <!-- Scene Sync Section -->
            @if (collabService.currentRoomId()) {
                <div class="section scene-sync-section">
                    <div class="section-header">
                        <span class="sync-header-icon">&#128260;</span>
                        <span class="section-title">Sahne Senkronizasyon</span>
                        <label class="toggle-switch" title="Sahne senkronizasyonu">
                            <input type="checkbox" [(ngModel)]="sceneSyncEnabled" />
                            <span class="toggle-slider"></span>
                        </label>
                    </div>

                    @if (sceneSyncEnabled) {
                        <!-- Sync Status -->
                        <div class="sync-status-bar">
                            <span class="sync-status-dot" [class.active]="sceneSyncEnabled"></span>
                            <span>{{ syncOperationCount }} islem senkronize edildi</span>
                        </div>

                        <!-- Object Operation Buttons -->
                        <div class="scene-sync-tools">
                            <button
                                class="fiko-btn tool scene-tool"
                                [class.active]="activeSyncMode === 'transform'"
                                (click)="toggleSyncMode('transform')"
                                title="Transform Senkronizasyonu"
                            >
                                <span>&#8634;</span>
                            </button>
                            <button
                                class="fiko-btn tool scene-tool"
                                [class.active]="activeSyncMode === 'visibility'"
                                (click)="toggleSyncMode('visibility')"
                                title="Gorunurluk Senkronizasyonu"
                            >
                                <span>&#128065;</span>
                            </button>
                            <button
                                class="fiko-btn tool scene-tool"
                                [class.active]="activeSyncMode === 'color'"
                                (click)="toggleSyncMode('color')"
                                title="Renk Senkronizasyonu"
                            >
                                <span>&#127912;</span>
                            </button>
                            <button
                                class="fiko-btn tool scene-tool"
                                [class.active]="activeSyncMode === 'delete'"
                                (click)="toggleSyncMode('delete')"
                                title="Silme Senkronizasyonu"
                            >
                                <span>&#128465;</span>
                            </button>
                        </div>

                        @if (activeSyncMode) {
                            <div class="markup-hint">
                                @switch (activeSyncMode) {
                                    @case ('transform') {
                                        <span>Nesne tasima/dondurme diger kullanicilara yansir</span>
                                    }
                                    @case ('visibility') {
                                        <span>Gorunurluk degisiklikleri paylasilir</span>
                                    }
                                    @case ('color') {
                                        <span>Renk degisiklikleri paylasilir</span>
                                    }
                                    @case ('delete') {
                                        <span>Silme islemleri diger kullanicilara yansir</span>
                                    }
                                }
                            </div>
                        }

                        <!-- Recent Sync Log -->
                        @if (recentSyncLog.length > 0) {
                            <div class="sync-log">
                                <div class="sync-log-header">Son Islemler</div>
                                @for (log of recentSyncLog; track log.timestamp) {
                                    <div class="sync-log-item" [class]="'sync-type-' + log.type">
                                        <span class="sync-log-icon">{{ getSyncLogIcon(log.type) }}</span>
                                        <span class="sync-log-text">{{ log.message }}</span>
                                        <span class="sync-log-time">{{ formatLogTime(log.timestamp) }}</span>
                                    </div>
                                }
                            </div>
                        }
                    }
                </div>
            }

            <!-- File Share Section -->
            @if (collabService.currentRoomId()) {
                <div class="section file-share-section">
                    <div class="section-header">
                        <span class="sync-header-icon">&#128193;</span>
                        <span class="section-title">Dosya Paylasim</span>
                    </div>

                    <!-- Upload Area -->
                    <div
                        class="file-drop-zone"
                        [class.dragging]="isDragging"
                        (dragover)="onDragOver($event)"
                        (dragleave)="onDragLeave($event)"
                        (drop)="onFileDrop($event)"
                        (click)="fileInput.click()"
                    >
                        <input
                            #fileInput
                            type="file"
                            accept=".step,.stp,.iges,.igs,.obj,.stl"
                            (change)="onFileSelected($event)"
                            style="display: none;"
                        />
                        <span class="drop-icon">&#128228;</span>
                        <span class="drop-text">Model dosyasi surukle veya tikla</span>
                        <span class="drop-formats">STEP, STP, IGES, OBJ, STL</span>
                    </div>

                    <!-- Upload Progress -->
                    @if (uploadProgress > 0 && uploadProgress < 100) {
                        <div class="upload-progress">
                            <div class="progress-bar">
                                <div class="progress-fill" [style.width.%]="uploadProgress"></div>
                            </div>
                            <span class="progress-text">{{ uploadFileName }} - %{{ uploadProgress }}</span>
                        </div>
                    }

                    <!-- Shared Files List -->
                    @if (sharedFiles.length > 0) {
                        <div class="shared-files">
                            <div class="shared-files-header">Paylasilan Dosyalar</div>
                            @for (file of sharedFiles; track file.fileUrl) {
                                <div class="shared-file-item">
                                    <span class="file-icon">{{ getFileIcon(file.fileType) }}</span>
                                    <div class="file-info">
                                        <span class="file-name">{{ file.fileName }}</span>
                                        <span class="file-meta">{{ file.userName }} - {{ formatFileSize(file.fileSize) }}</span>
                                    </div>
                                    <button
                                        class="fiko-btn small primary"
                                        (click)="downloadSharedFile(file)"
                                        title="Dosyayi Indir ve Sahneye Ekle"
                                    >
                                        <span>&#11015;</span>
                                    </button>
                                </div>
                            }
                        </div>
                    }
                </div>
            }

            <!-- Markup Tools Section -->
            @if (collabService.currentRoomId()) {
                <div class="section markup-section">
                    <div class="section-header">
                        <i class="paint brush icon"></i>
                        <span class="section-title">Cizim Araclari</span>
                    </div>

                    <div class="markup-tools">
                        <button
                            class="fiko-btn tool"
                            [class.active]="activeMarkupTool === 'arrow'"
                            (click)="selectMarkupTool('arrow')"
                            title="Ok Ciz"
                        >
                            <i class="long arrow alternate right icon"></i>
                        </button>
                        <button
                            class="fiko-btn tool"
                            [class.active]="activeMarkupTool === 'line'"
                            (click)="selectMarkupTool('line')"
                            title="Cizgi Ciz"
                        >
                            <i class="minus icon"></i>
                        </button>
                        <button
                            class="fiko-btn tool"
                            [class.active]="activeMarkupTool === 'polyline'"
                            (click)="selectMarkupTool('polyline')"
                            title="Coklu Cizgi"
                        >
                            <i class="bezier curve icon"></i>
                        </button>
                        <button
                            class="fiko-btn tool"
                            [class.active]="activeMarkupTool === 'text'"
                            (click)="selectMarkupTool('text')"
                            title="Yazi Ekle"
                        >
                            <i class="font icon"></i>
                        </button>

                        <div class="tool-separator"></div>

                        <button
                            class="fiko-btn tool danger"
                            (click)="clearMyMarkups()"
                            title="Cizimlerimi Temizle"
                        >
                            <i class="trash icon"></i>
                        </button>
                    </div>

                    @if (activeMarkupTool === 'text') {
                        <div class="text-input-row">
                            <input
                                type="text"
                                class="fiko-input"
                                placeholder="Yazi..."
                                [(ngModel)]="markupText"
                            />
                        </div>
                    }

                    @if (activeMarkupTool) {
                        <div class="markup-hint">
                            @switch (activeMarkupTool) {
                                @case ('arrow') {
                                    <span>Baslangic ve bitis noktasina tiklayin</span>
                                }
                                @case ('line') {
                                    <span>Iki nokta secin</span>
                                }
                                @case ('polyline') {
                                    <span>Noktalar secin, cift tikla bitirin</span>
                                }
                                @case ('text') {
                                    <span>Konum icin tiklayin</span>
                                }
                            }
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: [`
        .collab-dialog {
            min-width: 200px;
            max-width: 260px;
            font-size: 10px;
            color: #fff;
            text-shadow: 0 0 8px rgba(0,255,255,0.6);
        }

        .section {
            margin-bottom: 8px;
            padding: 6px;
            background: linear-gradient(135deg, rgba(18,22,30,0.95), rgba(25,30,42,0.95));
            border: 1px solid rgba(0,255,255,0.3);
            border-radius: 4px;
        }

        .section:last-child {
            margin-bottom: 0;
        }

        .section-header {
            display: flex;
            align-items: center;
            gap: 4px;
            margin-bottom: 6px;
            padding-bottom: 4px;
            border-bottom: 1px solid rgba(0,255,255,0.2);
        }

        .section-header i {
            font-size: 10px;
            color: #0ff;
        }

        .section-title {
            font-weight: bold;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .status-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #666;
        }

        .status-dot.online {
            background: #0f0;
            box-shadow: 0 0 6px #0f0;
        }

        .fiko-input {
            width: 100%;
            padding: 4px 6px;
            font-size: 10px;
            border: 1px solid rgba(0,255,255,0.3);
            border-radius: 3px;
            background: rgba(0,0,0,0.4);
            color: #fff;
            outline: none;
            margin-bottom: 4px;
        }

        .fiko-input:focus {
            border-color: rgba(0,255,255,0.6);
            box-shadow: 0 0 4px rgba(0,255,255,0.3);
        }

        .fiko-input::placeholder {
            color: rgba(255,255,255,0.4);
        }

        .fiko-btn {
            padding: 4px 8px;
            font-size: 9px;
            border: 1px solid rgba(0,255,255,0.3);
            border-radius: 3px;
            background: rgba(0,255,255,0.1);
            color: #0ff;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            transition: all 0.15s;
        }

        .fiko-btn i {
            font-size: 10px;
            margin: 0;
        }

        .fiko-btn:hover {
            background: rgba(0,255,255,0.2);
            border-color: rgba(0,255,255,0.5);
        }

        .fiko-btn.primary {
            background: rgba(0,255,255,0.2);
        }

        .fiko-btn.primary:hover {
            background: rgba(0,255,255,0.35);
        }

        .fiko-btn.danger {
            border-color: rgba(255,100,100,0.4);
            color: #f66;
            background: rgba(255,100,100,0.1);
        }

        .fiko-btn.danger:hover {
            background: rgba(255,100,100,0.25);
        }

        .fiko-btn.warning {
            border-color: rgba(255,200,100,0.4);
            color: #fc6;
            background: rgba(255,200,100,0.1);
        }

        .fiko-btn.warning:hover {
            background: rgba(255,200,100,0.25);
        }

        .fiko-btn.small {
            padding: 3px 6px;
        }

        .fiko-btn.tool {
            width: 26px;
            height: 26px;
            padding: 0;
            justify-content: center;
        }

        .fiko-btn.tool.active {
            background: rgba(0,255,255,0.4);
            border-color: #0ff;
            box-shadow: 0 0 8px rgba(0,255,255,0.4);
        }

        .connection-form,
        .room-form {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .connection-info,
        .room-info {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 6px;
        }

        .user-label,
        .room-label {
            flex: 1;
            padding: 3px 6px;
            background: rgba(0,255,255,0.1);
            border-radius: 3px;
            font-size: 10px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .users-section {
            padding: 0;
            background: transparent;
            border: none;
        }

        .markup-tools {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
        }

        .tool-separator {
            width: 1px;
            height: 20px;
            background: rgba(0,255,255,0.2);
            margin: 3px 4px;
        }

        .text-input-row {
            margin-top: 6px;
        }

        .markup-hint {
            margin-top: 6px;
            padding: 4px;
            background: rgba(0,100,100,0.2);
            border-radius: 3px;
            font-size: 9px;
            color: rgba(255,255,255,0.7);
            text-align: center;
        }

        /* Scene Sync Styles */
        .sync-header-icon {
            font-size: 11px;
        }

        .toggle-switch {
            position: relative;
            display: inline-block;
            width: 28px;
            height: 14px;
            margin-left: auto;
            cursor: pointer;
        }

        .toggle-switch input {
            opacity: 0;
            width: 0;
            height: 0;
        }

        .toggle-slider {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(255,255,255,0.15);
            border-radius: 7px;
            transition: 0.2s;
        }

        .toggle-slider::before {
            content: '';
            position: absolute;
            width: 10px;
            height: 10px;
            left: 2px;
            bottom: 2px;
            background: #888;
            border-radius: 50%;
            transition: 0.2s;
        }

        .toggle-switch input:checked + .toggle-slider {
            background: rgba(0,255,255,0.3);
        }

        .toggle-switch input:checked + .toggle-slider::before {
            transform: translateX(14px);
            background: #0ff;
            box-shadow: 0 0 4px #0ff;
        }

        .sync-status-bar {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 3px 6px;
            margin-bottom: 6px;
            background: rgba(0,0,0,0.2);
            border-radius: 3px;
            font-size: 9px;
            color: rgba(255,255,255,0.6);
        }

        .sync-status-dot {
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background: #666;
        }

        .sync-status-dot.active {
            background: #0f0;
            box-shadow: 0 0 4px #0f0;
            animation: pulse 1.5s infinite;
        }

        @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
        }

        .scene-sync-tools {
            display: flex;
            gap: 4px;
            margin-bottom: 4px;
        }

        .fiko-btn.scene-tool {
            flex: 1;
            font-size: 12px;
            height: 28px;
        }

        .fiko-btn.scene-tool.active {
            background: rgba(0,255,255,0.35);
            border-color: #0ff;
            box-shadow: 0 0 6px rgba(0,255,255,0.3);
        }

        .sync-log {
            margin-top: 6px;
            max-height: 80px;
            overflow-y: auto;
        }

        .sync-log-header {
            font-size: 8px;
            color: rgba(255,255,255,0.4);
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 3px;
        }

        .sync-log-item {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 2px 4px;
            font-size: 8px;
            border-left: 2px solid rgba(0,255,255,0.3);
            margin-bottom: 2px;
            background: rgba(0,0,0,0.15);
            border-radius: 0 2px 2px 0;
        }

        .sync-log-item.sync-type-transform { border-left-color: #0ff; }
        .sync-log-item.sync-type-visibility { border-left-color: #ff0; }
        .sync-log-item.sync-type-color { border-left-color: #f0f; }
        .sync-log-item.sync-type-delete { border-left-color: #f66; }
        .sync-log-item.sync-type-file { border-left-color: #0f0; }

        .sync-log-icon { font-size: 9px; width: 12px; }
        .sync-log-text { flex: 1; color: rgba(255,255,255,0.7); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .sync-log-time { color: rgba(255,255,255,0.3); font-size: 7px; }

        /* File Share Styles */
        .file-drop-zone {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 3px;
            padding: 10px 6px;
            border: 1px dashed rgba(0,255,255,0.3);
            border-radius: 4px;
            background: rgba(0,0,0,0.2);
            cursor: pointer;
            transition: all 0.2s;
        }

        .file-drop-zone:hover,
        .file-drop-zone.dragging {
            border-color: rgba(0,255,255,0.7);
            background: rgba(0,255,255,0.08);
            box-shadow: 0 0 8px rgba(0,255,255,0.2);
        }

        .drop-icon { font-size: 16px; }
        .drop-text { font-size: 9px; color: rgba(255,255,255,0.7); }
        .drop-formats { font-size: 7px; color: rgba(255,255,255,0.4); }

        .upload-progress {
            margin-top: 6px;
        }

        .progress-bar {
            width: 100%;
            height: 4px;
            background: rgba(0,0,0,0.3);
            border-radius: 2px;
            overflow: hidden;
        }

        .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #0ff, #0f0);
            border-radius: 2px;
            transition: width 0.3s;
        }

        .progress-text {
            display: block;
            font-size: 8px;
            color: rgba(255,255,255,0.5);
            margin-top: 2px;
            text-align: center;
        }

        .shared-files {
            margin-top: 6px;
        }

        .shared-files-header {
            font-size: 8px;
            color: rgba(255,255,255,0.4);
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 3px;
        }

        .shared-file-item {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 4px;
            background: rgba(0,0,0,0.15);
            border-radius: 3px;
            margin-bottom: 3px;
        }

        .file-icon {
            font-size: 14px;
        }

        .file-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 1px;
            overflow: hidden;
        }

        .file-name {
            font-size: 9px;
            color: #0ff;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .file-meta {
            font-size: 7px;
            color: rgba(255,255,255,0.4);
        }
    `]
})
export class FikoCollabDialogComponent implements OnDestroy {
    collabService = inject(FikoCollabService);
    private cd = inject(ChangeDetectorRef);

    /** FikoDialogCore pencere referansi */
    private _pencere: any;
    @Input() set pencere(v: any) {
        this._pencere = v?.instance || v;
    }
    get pencere(): any { return this._pencere; }

    /** Scene reference for initialization */
    @Input() scene: THREE.Scene | null = null;
    @Input() camera: THREE.Camera | null = null;
    @Input() orbitControls: any = null;

    // Form state
    serverUrl: string = 'https://fikoyt.com:9000';
    userName: string = 'Kullanici_' + Math.floor(Math.random() * 1000);
    userId: string = 'user_' + Date.now() + '_' + Math.floor(Math.random() * 10000);
    roomId: string = 'default-room';
    projectId: string = 'default-project';

    // Markup tool state
    activeMarkupTool: MarkupType | null = null;
    markupText: string = '';

    // Scene sync state
    sceneSyncEnabled: boolean = true;
    activeSyncMode: SceneOperationType | null = null;
    syncOperationCount: number = 0;
    recentSyncLog: { type: string; message: string; timestamp: number }[] = [];

    // File share state
    isDragging: boolean = false;
    uploadProgress: number = 0;
    uploadFileName: string = '';
    sharedFiles: FileShareReadyData[] = [];

    /** Public callback for file import (set by parent component) */
    onFileImportRequest: ((file: File) => void) | null = null;

    ngOnDestroy(): void {
        // Cleanup
        this.collabService.onFileShareReady = null;
    }

    connect(): void {
        if (!this.scene || !this.camera) {
            console.warn('[CollabDialog] Scene or camera not provided');
            return;
        }

        if (!this.serverUrl || !this.userName) {
            return;
        }

        this.collabService.initialize(this.scene, this.camera, this.serverUrl, this.orbitControls);
    }

    disconnect(): void {
        this.collabService.disconnect();
    }

    joinRoom(): void {
        if (!this.roomId) return;
        this.collabService.joinRoom(this.roomId, this.projectId, this.userId, this.userName);
        this.setupFileShareListener();
    }

    leaveRoom(): void {
        this.collabService.leaveRoom();
    }

    selectMarkupTool(tool: MarkupType): void {
        if (this.activeMarkupTool === tool) {
            this.activeMarkupTool = null;
        } else {
            this.activeMarkupTool = tool;
        }
        this.cd.detectChanges();
    }

    clearMyMarkups(): void {
        this.collabService.clearMyMarkups();
    }

    // =========================================================================
    // Scene Sync Methods
    // =========================================================================

    toggleSyncMode(mode: SceneOperationType): void {
        this.activeSyncMode = this.activeSyncMode === mode ? null : mode;
        this.cd.detectChanges();
    }

    addSyncLog(type: string, message: string): void {
        this.recentSyncLog.unshift({ type, message, timestamp: Date.now() });
        if (this.recentSyncLog.length > 10) {
            this.recentSyncLog.pop();
        }
        this.syncOperationCount++;
        this.cd.detectChanges();
    }

    getSyncLogIcon(type: string): string {
        switch (type) {
            case 'transform': return '\u21BA';   // ↺
            case 'visibility': return '\uD83D\uDC41'; // 👁
            case 'color': return '\uD83C\uDFA8';      // 🎨
            case 'delete': return '\uD83D\uDDD1';     // 🗑
            case 'file': return '\uD83D\uDCC1';       // 📁
            default: return '\u2022';                   // •
        }
    }

    formatLogTime(timestamp: number): string {
        const elapsed = Date.now() - timestamp;
        if (elapsed < 5000) return 'simdi';
        if (elapsed < 60000) return `${Math.floor(elapsed / 1000)}sn`;
        return `${Math.floor(elapsed / 60000)}dk`;
    }

    // =========================================================================
    // File Share Methods
    // =========================================================================

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
    }

    onFileDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.handleFileUpload(files[0]);
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.handleFileUpload(input.files[0]);
            input.value = '';
        }
    }

    handleFileUpload(file: File): void {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        const validExts = ['step', 'stp', 'iges', 'igs', 'obj', 'stl'];

        if (!validExts.includes(ext)) {
            console.warn('[CollabDialog] Gecersiz dosya formati:', ext);
            return;
        }

        this.uploadFileName = file.name;
        this.uploadProgress = 0;

        // Upload file to server
        const formData = new FormData();
        formData.append('file', file);
        formData.append('roomId', this.collabService.currentRoomId() || '');
        formData.append('userId', this.userId);
        formData.append('userName', this.userName);

        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
                this.uploadProgress = Math.round((e.loaded / e.total) * 100);
                this.cd.detectChanges();
            }
        };

        xhr.onload = () => {
            if (xhr.status === 200) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    const fileUrl = response.fileUrl || response.url || '';

                    // Notify other users
                    this.collabService.emitFileShareReady(
                        file.name,
                        fileUrl,
                        file.size,
                        ext
                    );

                    this.addSyncLog('file', `${file.name} paylasild`);
                    this.uploadProgress = 100;
                    setTimeout(() => { this.uploadProgress = 0; this.cd.detectChanges(); }, 2000);
                } catch (e) {
                    console.error('[CollabDialog] Upload response parse error:', e);
                }
            } else {
                console.error('[CollabDialog] Upload failed:', xhr.status);
                this.uploadProgress = 0;
            }
            this.cd.detectChanges();
        };

        xhr.onerror = () => {
            console.error('[CollabDialog] Upload error');
            this.uploadProgress = 0;
            this.cd.detectChanges();
        };

        const serverBase = this.serverUrl.replace(/\/$/, '');
        xhr.open('POST', `${serverBase}/api/collab/upload`);
        xhr.send(formData);
    }

    downloadSharedFile(file: FileShareReadyData): void {
        this.addSyncLog('file', `${file.fileName} indiriliyor...`);

        fetch(file.fileUrl)
            .then(res => res.blob())
            .then(blob => {
                const importFile = new File([blob], file.fileName, { type: blob.type });
                if (this.onFileImportRequest) {
                    this.onFileImportRequest(importFile);
                    this.addSyncLog('file', `${file.fileName} sahneye eklendi`);
                } else {
                    console.warn('[CollabDialog] onFileImportRequest callback not set');
                }
            })
            .catch(err => {
                console.error('[CollabDialog] Download failed:', err);
                this.addSyncLog('file', `${file.fileName} indirme hatasi`);
            });
    }

    getFileIcon(fileType: string): string {
        switch (fileType?.toLowerCase()) {
            case 'step': case 'stp': return '\uD83D\uDCCF';  // 📏
            case 'iges': case 'igs': return '\uD83D\uDCC0';  // 📀
            case 'obj': return '\uD83E\uDDF1';                 // 🧱
            case 'stl': return '\u25B3';                        // △
            default: return '\uD83D\uDCC4';                    // 📄
        }
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    }

    /**
     * Setup file share listener from service
     * Called after connect/joinRoom to listen for incoming shared files
     */
    setupFileShareListener(): void {
        this.collabService.onFileShareReady = (data: FileShareReadyData) => {
            // Don't add our own uploads
            if (data.userId === this.userId) return;

            this.sharedFiles.unshift(data);
            if (this.sharedFiles.length > 20) this.sharedFiles.pop();
            this.addSyncLog('file', `${data.userName}: ${data.fileName}`);
            this.cd.detectChanges();
        };
    }
}
